import Vue from "vue";
import firebase from "firebase";
import FirebaseModel, { FirebaseModelData, refAsString } from "@/models/FirebaseModel";
import SolicitacaoModel, { SolicitacaoDados } from "@/models/Solicitacao";
import { UserModule, store } from "@/store";
import { getModule } from "vuex-module-decorators";
import UserModel, { UserDb, USERS_COLLECTION } from "@/models/User";
import ChatModel, { CHAT_COLLECTION, ChatDados } from "@/models/Chat";
import _debug from "debug";

const debug = _debug("Mediacao:models:Processo.ts");
export const PROCESSOS_COLLECTION = "processos";

const userModule = getModule(UserModule, store);

function fromRef2String(dados: any): ProcessoDados {
    if (dados.userMediador) {
        dados.userMediador = dados.userMediador!.id;
    }
    if (dados.userParteUm) {
        dados.userParteUm = dados.userParteUm!.id;
    }
    if (dados.userParteDois) {
        dados.userParteDois = dados.userParteDois!.id;
    }
    dados = FirebaseModel.fromRef2String(dados);
    return dados;
}

export interface ProcessoUsuariosRef {
    userParteUm?: string;
    userParteDois?: string;
    userMediador?: string;
}

export const enum ParteChaves {
    ParteUm = "userParteUm",
    ParteDois = "userParteDois",
    ParteMediador = "userMediador",
}

export interface ProcessoDados extends FirebaseModelData, ProcessoUsuariosRef {
    id?: string;
    numero?: number;
    titulo: string;
    status: string;
    resumo: string;
    obervacoes?: string;
}

export interface ProcessoPartes {
    parteUm: UserDb | null;
    parteDois: UserDb | null;
    mediador: UserDb | null;
}

export default class ProcessoModel extends FirebaseModel {
    public get titulo(): string {
        return this.myData.titulo;
    }
    public set titulo(titulo: string) {
        this.myData.titulo = titulo;
    }

    public get status() {
        return this.myData.status;
    }

    public set status(theNewStatus: string) {
        this.myData.status = theNewStatus;
    }

    public get id(): string | undefined {
        return this.myData.id;
    }
    public set id(id: string | undefined) {
        this.myData.id = id;
    }

    public get resumo() {
        return this.myData.resumo;
    }

    public set resumo(resumo: string) {
        this.myData.resumo = resumo;
    }

    public get observacoes() {
        if (this.myData.obervacoes) {
            return this.myData.obervacoes;
        } else {
            return "";
        }
    }
    public set observacoes(obervacoes: string) {
        this.myData.obervacoes = obervacoes;
    }

    public get partes(): ProcessoPartes | null {
        return this.minhasPartes;
    }
    public set partes(asPartes: ProcessoPartes | null) {
        this.minhasPartes = asPartes;
    }

    public get partesPromise(): Promise<ProcessoPartes> | null {
        return this.minhasPartesPromise;
    }
    public set partesPromise(asPartes: Promise<ProcessoPartes> | null) {
        this.minhasPartesPromise = asPartes;
    }

    public get chats(): ChatModel[] | null {
        return this.myChats;
    }

    public get temParteUm(): boolean {
        if (this.minhasPartes && this.minhasPartes.parteUm) {
            return true;
        }
        return false;
    }
    public get temParteDois(): boolean {
        if (this.minhasPartes && this.minhasPartes.parteDois) {
            return true;
        }
        return false;
    }
    public static fromRef2String(dados: any) {
        return fromRef2String(dados);
    }

    public static async fetchById(id: string, withPartes = false): Promise<ProcessoModel> {
        const ref = firebase
            .firestore()
            .collection(PROCESSOS_COLLECTION)
            .doc(id);

        const processo = await ref.get();
        if (processo.exists) {
            return this.fromQuerySnapshot(processo, withPartes);
        } else {
            throw new Error(`Processo não econtrado!(id:${id})`);
        }
    }

    public static async listByUser(userId: string): Promise<ProcessoModel[]> {
        debug("ProcessoModel.listByUser: userId=" + userId);
        return new Promise((resolve, reject) => {
            const processos: ProcessoModel[] = [];
            const collection = firebase.firestore().collection(PROCESSOS_COLLECTION);
            collection
                .where(
                    "userParteUm",
                    "==",
                    firebase
                        .firestore()
                        .collection(USERS_COLLECTION)
                        .doc(userId),
                )
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        processos.push(this.fromQuerySnapshot(doc));
                    });
                    collection
                        .where(
                            "userParteDois",
                            "==",
                            firebase
                                .firestore()
                                .collection(USERS_COLLECTION)
                                .doc(userId),
                        )
                        .get()
                        .then(querySnapshot2 => {
                            querySnapshot2.forEach(doc => {
                                processos.push(this.fromQuerySnapshot(doc));
                            });
                            collection
                                .where(
                                    "userMediador",
                                    "==",
                                    firebase
                                        .firestore()
                                        .collection(USERS_COLLECTION)
                                        .doc(userId),
                                )
                                .get()
                                .then(querySnapshot3 => {
                                    querySnapshot3.forEach(doc => {
                                        processos.push(this.fromQuerySnapshot(doc));
                                    });
                                    resolve(processos);
                                })
                                .catch(error => {
                                    reject(error);
                                });
                        })
                        .catch(error => {
                            reject(error);
                        });
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    public static async newFromSolicitacao(solicitacaoId: string): Promise<ProcessoModel> {
        debug("ProcessoModel vai criar da solicitacao " + solicitacaoId);
        const solicitacaoRef = firebase
            .firestore()
            .collection("solicitacoes")
            .doc(solicitacaoId);
        const userDb = userModule.getUser.userDb;
        const processoRef = firebase
            .firestore()
            .collection(PROCESSOS_COLLECTION)
            .doc();
        return new Promise((resolve, reject) => {
            firebase
                .firestore()
                .runTransaction(transaction => {
                    return transaction.get(solicitacaoRef).then(solicitacaoDoc => {
                        if (!solicitacaoDoc.exists) {
                            throw new Error(`Solicitação(${solicitacaoId}) não existe!`);
                        }
                        const solicitacaoData = solicitacaoDoc.data();
                        const novoProcesso = this.newFromSolicitacaoData(solicitacaoData as SolicitacaoDados);
                        transaction.set(processoRef, novoProcesso).delete(solicitacaoRef);
                    });
                })
                .then(() => {
                    processoRef.get().then(processoFromDb => {
                        const processoData = Object.assign(processoFromDb.data(), {
                            id: processoFromDb.id,
                        });
                        resolve(new ProcessoModel(fromRef2String(processoData) as ProcessoDados, true));
                    });
                });
        });
    }

    public static fromQuerySnapshot(doc: firebase.firestore.DocumentSnapshot, withPartes = false) {
        const dados: unknown = Object.assign({ id: doc.id }, doc.data());
        return new ProcessoModel(fromRef2String(dados) as ProcessoDados, withPartes);
    }

    private static newFromSolicitacaoData(data: SolicitacaoDados): ProcessoDados {
        const userDb = userModule.getUser.userDb;
        if (!userDb) {
            throw new Error("Não foi possível obter o usuário do Mediador para criar o processo");
        }
        const mediadorRef = {
            userMediador: firebase
                .firestore()
                .collection(USERS_COLLECTION)
                .doc(userDb.email),
        };
        const processo = Object.assign({}, data, mediadorRef, {
            creator: mediadorRef.userMediador,
        });
        return processo;
    }
    public userId?: string;

    protected myData: ProcessoDados;

    protected myChats: ChatModel[] | null = null;

    private minhasPartes: ProcessoPartes | null = null;
    private minhasPartesPromise: Promise<ProcessoPartes> | null = null;

    constructor(dados?: ProcessoDados, fetchPartes = false) {
        super(PROCESSOS_COLLECTION);
        if (!dados) {
            this.myData = {
                id: undefined,
                titulo: "",
                status: "aberto",
                resumo: "",
                obervacoes: "",
            };
        } else {
            this.myData = dados;
        }

        if (fetchPartes) {
            this.minhasPartesPromise = this._fetchPartes(this.myData).then((partesDados: ProcessoPartes) => {
                debug("buscou as partes", partesDados);
                this.minhasPartes = partesDados;
                return partesDados;
            });
        }
    }
    public async fazParte(user: UserDb): Promise<boolean> {
        const partes = await this.minhasPartesPromise;
        if (partes) {
            if (partes.parteUm && partes.parteUm.email === user.email) {
                return true;
            }
            if (partes.parteDois && partes.parteDois.email === user.email) {
                return true;
            }
            if (partes.mediador && partes.mediador.email === user.email) {
                return true;
            }
        }
        return false;
    }

    public setParte(parte: ParteChaves, userId: string) {
        const parteRef = firebase
            .firestore()
            .collection(USERS_COLLECTION)
            .doc(userId);
        this.myData[parte] = userId;
    }
    public async removeParte(parte: ParteChaves) {
        this.myData[parte] = undefined;
        if (this.myData.id) {
            const ref = this.getRef({ id: this.myData.id });
            const fieldDelete = {};
            (fieldDelete as any)[parte] = firebase.firestore.FieldValue.delete();
            await ref.update(fieldDelete);
            return (this.minhasPartesPromise = this._fetchPartes(this.myData).then((partesDados: ProcessoPartes) => {
                debug("buscou as partes", partesDados);
                this.minhasPartes = partesDados;
                return partesDados;
            }));
        }
    }

    public async add(dados: ProcessoDados = this.myData, userRef: ProcessoUsuariosRef): Promise<ProcessoModel> {
        debug("tentando adicionar processo", dados);
        const mediacaoRef = this.getRef(dados);
        const mediacao = await mediacaoRef.get();
        if (mediacao.exists) {
            throw new Error("Esse processo já existe");
        } else {
            const dadosParaSalvar = Object.assign({}, dados, userRef);
            delete dadosParaSalvar.id;
            const dadosSalvos = await super._add(dadosParaSalvar);
            const result = new ProcessoModel(fromRef2String(dadosSalvos) as ProcessoDados);
            return result;
        }
    }

    public async addChat(): Promise<ChatModel> {
        return this.closeAllChats().then(async () => {
            const processoRef = this.getRef(this.myData);

            const theRef = await processoRef.collection(CHAT_COLLECTION).add({ data: new Date(), status: "aberto" });

            const savedData = await theRef.get();
            const dadosChat = Object.assign({ id: savedData.id }, savedData.data()) as ChatDados;
            debug("Processo.addChat, this", this);
            const chatModel = new ChatModel(dadosChat, processoRef, this);
            if (!this.myChats) {
                this.myChats = [];
            }
            this.myChats.push(chatModel);
            return chatModel;
        });
    }

    public async loadChats() {
        const processoRef = this.getRef(this.myData);

        const chats = await processoRef.collection(CHAT_COLLECTION).get();
        const theChats: ChatModel[] = chats.docs.reduce<ChatModel[]>((acc: ChatModel[], current: firebase.firestore.QueryDocumentSnapshot) => {
            const data = Object.assign({ id: current.id }, current.data()) as ChatDados;
            const chatM = new ChatModel(data, processoRef, this);
            acc.push(chatM);
            return acc;
        }, []);

        this.myChats = theChats;
        return theChats;
    }

    public async save(creator?: string) {
        console.log("Processo save, creator", creator);
        if (creator) {
            this.myData.creatorRef = creator;
        }
        if (this.id) {
            const firebaseTypedData = this.fixFirebaseTypes(this.myData);
            const savedData = await super.update(firebaseTypedData);
            Object.assign(this.myData, savedData);
            return savedData;
        } else {
            const dataToBeSaved: any = {
                titulo: this.myData.titulo,
                status: this.myData.status,
                resumo: this.myData.resumo,
                creator: firebase
                    .firestore()
                    .collection(USERS_COLLECTION)
                    .doc(creator),
                userMediador: firebase
                    .firestore()
                    .collection(USERS_COLLECTION)
                    .doc(creator),
            };

            const savedData = await super.update(dataToBeSaved);
            Object.assign(this.myData, savedData);
            this.minhasPartesPromise = this._fetchPartes(this.myData).then((partesDados: ProcessoPartes) => {
                debug("buscou as partes", partesDados);
                this.minhasPartes = partesDados;
                return partesDados;
            });
            return savedData;
        }
    }

    private fixFirebaseTypes(data: ProcessoDados) {
        const fixedData: any = Object.assign({}, data);
        if (typeof data.userMediador === "string") {
            fixedData.userMediador = firebase
                .firestore()
                .collection(USERS_COLLECTION)
                .doc(data.userMediador);
        }
        if (typeof data.userParteUm === "string") {
            fixedData.userParteUm = firebase
                .firestore()
                .collection(USERS_COLLECTION)
                .doc(data.userParteUm);
        }
        if (typeof data.userParteDois === "string") {
            fixedData.userParteDois = firebase
                .firestore()
                .collection(USERS_COLLECTION)
                .doc(data.userParteDois);
        }
        return fixedData;
    }

    private async closeAllChats(): Promise<void> {
        const processoRef = this.getRef(this.myData);
        const batch = firebase.firestore().batch();

        if (this.myChats) {
            for (const chat of this.myChats) {
                batch.update(firebase.firestore().doc(`/${PROCESSOS_COLLECTION}/${this.myData.id}/${CHAT_COLLECTION}/${chat.id}`), { status: "encerrado" });
            }
        }

        return batch.commit();
    }

    private async _fetchPartes(dados: ProcessoUsuariosRef): Promise<ProcessoPartes> {
        let parteUm = null;
        if (this.myData.userParteUm) {
            const parteUmRef = await firebase
                .firestore()
                .collection(USERS_COLLECTION)
                .doc(refAsString(this.myData.userParteUm))
                .get();
            parteUm = parteUmRef.data() as UserDb;
        }
        let parteDois = null;
        if (this.myData.userParteDois) {
            const parteDoisRef = await firebase
                .firestore()
                .collection(USERS_COLLECTION)
                .doc(refAsString(this.myData.userParteDois))
                .get();
            parteDois = parteDoisRef.data() as UserDb;
        }
        let mediador = null;
        if (this.myData.userMediador) {
            let id: any = this.myData.userMediador;
            if (typeof id !== "string") {
                id = id.id;
            }
            const mediadorRef = await firebase
                .firestore()
                .collection(USERS_COLLECTION)
                .doc(refAsString(id))
                .get();
            mediador = mediadorRef.data() as UserDb;
        }
        return { parteUm, parteDois, mediador };
    }

    private getRef(dados?: { id?: string }): firebase.firestore.DocumentReference {
        let ref = firebase
            .firestore()
            .collection(PROCESSOS_COLLECTION)
            .doc();
        if (dados && dados.id) {
            ref = firebase
                .firestore()
                .collection(PROCESSOS_COLLECTION)
                .doc(dados.id);
        }
        return ref;
    }
}
